import { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useUsersClient } from "@/hooks/useDataClient";
import type { User } from "@/types/auth";
import type { Subscription, OnboardingStatus } from "@dnplace/types";
import { useAuth as useClerkAuth, useUser as useClerkUser, useOrganizationList, useSession, useOrganization } from "@clerk/clerk-react";
import { setUserContext, addBreadcrumb } from "@/lib/sentry";
import { updateConvexAuth } from "@/utils/convex";

type AuthContextType = {
  user: User | null;
  subscription: Subscription | null;
  onboarding: OnboardingStatus | null;
  isLoading: boolean;
  hasActiveSubscription: boolean;
  hasCompletedOnboarding: boolean;
  login: () => Promise<boolean>;
  register: () => Promise<boolean>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded, signOut } = useClerkAuth();
  const { user: clerkUser } = useClerkUser();
  const { session } = useSession();
  const { organization: activeOrg } = useOrganization(); // Get active organization directly
  const { isLoaded: orgsLoaded, setActive, createOrganization, userMemberships } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  // ✅ Use data client for user operations
  const usersClient = useUsersClient();
  const [user, setUser] = useState<User | null>(null);
  const [ensuring, setEnsuring] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false); // Prevent duplicate org creation
  const cancelledRef = useRef(false); // Use ref to avoid closure issues with cleanup
  
  // Fetch subscription and onboarding status
  // ✅ Only query when user has an organization in Clerk
  // This prevents queries from running before org is created
  // Use activeOrg from useOrganization() instead of userMemberships.data.length
  // because useOrganization() updates immediately after setActive()
  const hasOrg = !!activeOrg;
  
  console.log("🔍 Query conditions:", { 
    user: !!user, 
    isSignedIn, 
    hasOrg,
    willQuerySubscription: user && isSignedIn && hasOrg,
    willQueryOnboarding: user && isSignedIn && hasOrg,
  });
  
  const subscription = useQuery(
    api.subscriptions.getUserSubscription,
    user && isSignedIn && hasOrg ? {} : "skip"
  ) as Subscription | null | undefined;
  
  const onboarding = useQuery(
    api.onboarding.getOnboardingStatus,
    user && isSignedIn && hasOrg ? {} : "skip"
  ) as OnboardingStatus | null | undefined;
  
  console.log("🔍 Query results:", { subscription, onboarding });

  useEffect(() => {
    // Reset cancellation flag at the start of each effect
    cancelledRef.current = false;
    
    async function run() {
      if (!isLoaded || !orgsLoaded) return;
      if (!isSignedIn || !clerkUser) {
        setUser(null);
        return;
      }
      
      // Prevent re-entry - don't run if already ensuring or user already set
      // This is critical because setActive() changes userMemberships, triggering a re-run
      if (ensuring || user) {
        console.log("⏭️ Skipping run (ensuring:", ensuring, "user:", !!user, ")");
        return;
      }
      
      // Debug: Log full userMemberships state
      console.log("🔍 userMemberships state:", {
        isLoaded: !userMemberships?.isLoading,
        hasData: !!userMemberships?.data,
        count: userMemberships?.data?.length || 0,
        isLoading: userMemberships?.isLoading,
      });
      
      // Wait for memberships to load before proceeding
      // Check isLoading instead of isLoaded (isLoaded may stay undefined)
      if (userMemberships?.isLoading !== false) {
        console.log("⏳ Waiting for organization memberships to load...");
        return;
      }
      
      setEnsuring(true);
      try {
        // 1. Ensure user has a Clerk organization (only once)
        const memberships = userMemberships?.data || [];
        console.log(`🏢 User has ${memberships.length} organization memberships`);
        
        // Only create org if:
        // - User has NO memberships
        // - We're not already creating
        // - We haven't failed due to org limit
        if (memberships.length === 0 && setActive && createOrganization && !creatingOrg) {
          setCreatingOrg(true);
          console.log("🏢 Creating Clerk organization for new user...");
          
          try {
            const username = clerkUser.username || clerkUser.firstName || clerkUser.emailAddresses?.[0]?.emailAddress?.split('@')[0] || 'user';
            const org = await createOrganization({
              name: `${username}'s Workspace`,
              slug: undefined, // Let Clerk auto-generate
            });
            console.log("✅ Clerk organization created:", org.id);
            
            // Set as active organization
            await setActive({ organization: org.id });
            console.log("✅ Organization set as active");
          } catch (orgError: unknown) {
            const error = orgError as Error;
            console.error("❌ Failed to create Clerk organization:", error.message);
            
            // If we hit org limit, don't retry - user needs to use existing org or upgrade Clerk
            if (error.message?.includes('limit') || error.message?.includes('50 organizations')) {
              console.error("⚠️ Organization limit reached. User must use existing organization or upgrade Clerk plan.");
              // Don't reset creatingOrg - prevent infinite retry
              throw new Error("Organization limit reached. Please contact support or upgrade your Clerk plan.");
            }
            
            // For other errors, allow retry
            setCreatingOrg(false);
            throw error;
          }
        }
        
        // 🔄 Ensure organization is set as active and force JWT refresh
        // This is critical: The JWT template needs an active org to populate org_id
        if (memberships.length > 0 && session && setActive) {
          const firstOrg = memberships[0].organization;
          console.log("🔄 Setting organization as active:", firstOrg.id);
          
          // Set the organization as active in the Clerk session
          await setActive({ organization: firstOrg.id });
          console.log("✅ Organization set as active");
          
          // Now force JWT refresh - it will include org_id
          console.log("🔄 Forcing JWT refresh to include org_id...");
          const token = await session.getToken({ template: 'convex', skipCache: true });
          console.log("✅ JWT refreshed, token:", token?.substring(0, 50) + "...");
          
          // ✅ FIX: Update BOTH Convex clients with the refreshed JWT
          console.log("🔄 Updating Convex clients with refreshed JWT containing org_id...");
          updateConvexAuth(async () => {
            return await session.getToken({ template: 'convex', skipCache: true });
          });
          // The data client will also update via useConvexClient's useEffect (it fetches and sets the token string)
          
          // Decode and inspect the JWT
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              console.log("🔍 JWT payload:", {
                org_id: payload.org_id,
                org_slug: payload.org_slug,
                org_role: payload.org_role,
              });
            } catch (e) {
              console.error("Failed to decode JWT:", e);
            }
          }
          console.log("✅ Global Convex client auth updated");
        }
        
        // 2. Create/update user record in Convex (NO org creation)
        console.log("📝 Calling ensureClerkUser with:", {
          hasClerkId: Boolean(clerkUser.id),
          hasEmail: Boolean(clerkUser.primaryEmailAddress?.emailAddress || clerkUser.emailAddresses?.[0]?.emailAddress || ""),
          hasUsername: Boolean(clerkUser.username || clerkUser.firstName || undefined),
        });

        console.log("🔄 Calling ensureClerkUser...");
        const u = await usersClient.ensureClerkUser({
          clerkId: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || clerkUser.emailAddresses?.[0]?.emailAddress || "",
          username: clerkUser.username || clerkUser.firstName || undefined,
        });
        console.log("🔍 ensureClerkUser returned:", u);
        console.log("✅ User returned:", u);
        console.log("📝 cancelledRef.current =", cancelledRef.current);
        if (!cancelledRef.current) {
          console.log("📝 Setting user...");
          setUser(u as User);
          console.log("✅ User set!");
          
          // Set Sentry user context
          setUserContext({
            id: u._id,
            email: u.email,
            username: u.username,
          });
          
          // Add breadcrumb for user authentication
          addBreadcrumb(
            `User authenticated: ${u.username || u.email}`,
            'auth',
            'info'
          );
          console.log("✅ Auth setup complete");
        }
      } catch (error) {
        console.error("❌ Error in AuthProvider:", error);
        if (!cancelledRef.current) {
          setEnsuring(false);
          setCreatingOrg(false); // Allow retry on error
        }
      } finally {
        if (!cancelledRef.current) setEnsuring(false);
      }
    }
    run();
    return () => { 
      console.log("🧹 Cleanup: setting cancelledRef.current = true");
      cancelledRef.current = true; 
    };
  }, [isLoaded, isSignedIn, clerkUser, usersClient, orgsLoaded, userMemberships?.isLoading, setActive, createOrganization, creatingOrg]);
  // Note: session and userMemberships.data.length are NOT in deps because setActive() changes them, 
  // which would cause premature cleanup before setUser() can execute

  const value: AuthContextType = useMemo(() => {
    // Only consider queries "loading" when we're actually querying them
    // When queries are skipped (no org yet), they return undefined but shouldn't block loading
    const shouldQuerySubscription = user && isSignedIn && hasOrg;
    const shouldQueryOnboarding = user && isSignedIn && hasOrg;
    const queriesLoading = 
      (shouldQuerySubscription && subscription === undefined) ||
      (shouldQueryOnboarding && onboarding === undefined);
    
    const finalIsLoading = Boolean(!isLoaded || ensuring || queriesLoading);
    
    console.log("🔍 AuthContext isLoading calculation:", {
      isLoaded,
      ensuring,
      queriesLoading,
      shouldQuerySubscription,
      shouldQueryOnboarding,
      hasUser: !!user,
      hasOrg,
      finalIsLoading,
    });
    
    return {
      user,
      subscription: subscription ?? null,
      onboarding: onboarding ?? null,
      isLoading: finalIsLoading,
      hasActiveSubscription: subscription?.status === 'active',
      // Only mark as incomplete if query has loaded and returned false
      // If onboarding is undefined (not loaded yet), treat as complete to avoid premature redirects
      hasCompletedOnboarding: onboarding === undefined ? true : (onboarding?.isComplete ?? false),
      login: async () => false, // unused under Clerk
      register: async () => false, // unused under Clerk
      logout: () => { signOut(); },
    };
  }, [user, subscription, onboarding, isLoaded, ensuring, signOut, isSignedIn, hasOrg]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}
